// Spooky-theme hero cards for status / bug / feature events. Each renders the shared haunted
// panel (text column on the left) + an animated scene occupying the right half. The scene artwork
// is injected verbatim from the design prototype (SpookyScenes.js); the text column is built from
// the real event data. Only rendered when the spooky theme is active (gated in MessageList).

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { SPOOKY_SCENES, SPOOKY_BUG, SPOOKY_FEATURE } from "./SpookyScenes.js";
import "../spooky-cards.css";

const norm = (s) => (s || "").replace(/\s+/g, "").toLowerCase();

// status (normalised) → scene archetype + copy. `person: true` → the byline names the changer.
const STATUS = {
  pending:               { scene: "crypt",     badge: "⚰️ Status update", title: "Slumbering, not summoned", act: "queued by",     who: "the coven" },
  inprogress:            { scene: "cauldron",  badge: "⚗️ Status update", title: "Stirring to life…",        act: "assigned to",   person: true },
  devtesting:            { scene: "wizard",    badge: "🪄 Status update", title: "Weaving the spellwork",     act: "tested by",     who: "the dev coven" },
  qa:                    { scene: "potion",    badge: "🧪 Status update", title: "Brew ready for tasting",    act: "brewed by",     person: true },
  clienttesting:         { scene: "eye",       badge: "🔍 Status update", title: "Under watchful eyes…",      act: "tested by",     who: "the client" },
  waitingbrief:          { scene: "crystal",   badge: "🔮 Status update", title: "Gazing into the mists…",    act: "awaiting",      who: "the brief" },
  awaitingapproval:      { scene: "decree",    badge: "📜 Status update", title: "Awaiting the seal",         act: "awaiting",      who: "the coven’s blessing" },
  awaitingclient:        { scene: "raven",     badge: "✉️ Status update", title: "The raven awaits reply",     act: "sent to",       who: "the client" },
  onhold:                { scene: "hourglass", badge: "⏳ Status update", title: "Time held in the glass",     act: "paused by",     who: "a stalled dependency" },
  releasedocsinprogress: { scene: "cauldron",  badge: "⚗️ Status update", title: "Scribing the release rites", act: "documented by", person: true },
  released:              { scene: "zombie",    badge: "⚡ Status update", title: "It's alive — released!",    act: "conjured by",   person: true },
  completed:             { scene: "zombie",    badge: "⚡ Status update", title: "It's alive!",               act: "conjured by",   person: true },
};

// Per-scene accent / tint / title-flicker (from the design tokens).
const SCENE = {
  cauldron:  { a: "#c9a3ff", bg: "rgba(160,107,255,.14)", bd: "rgba(160,107,255,.4)",  f: "P" },
  potion:    { a: "#8fc4ff", bg: "rgba(120,180,255,.14)", bd: "rgba(120,180,255,.4)",  f: "P" },
  eye:       { a: "#ff7518", bg: "rgba(255,117,24,.14)",  bd: "rgba(255,117,24,.4)",   f: "O" },
  hourglass: { a: "#a9c4ff", bg: "rgba(140,180,255,.14)", bd: "rgba(140,180,255,.42)", f: "P" },
  zombie:    { a: "#6bd968", bg: "rgba(107,217,104,.14)", bd: "rgba(107,217,104,.4)",  f: "G" },
  crypt:     { a: "#d8c79a", bg: "rgba(216,199,154,.14)", bd: "rgba(216,199,154,.42)", f: "O" },
  crystal:   { a: "#a9c4ff", bg: "rgba(140,180,255,.14)", bd: "rgba(140,180,255,.42)", f: "P" },
  decree:    { a: "#e0b96a", bg: "rgba(224,185,106,.14)", bd: "rgba(224,185,106,.44)", f: "O" },
  raven:     { a: "#9db4d8", bg: "rgba(157,180,216,.14)", bd: "rgba(157,180,216,.44)", f: "P" },
  wizard:    { a: "#c9a3ff", bg: "rgba(160,107,255,.14)", bd: "rgba(160,107,255,.42)", f: "P" },
  bug:       { a: "#ff5d8f", bg: "rgba(255,93,143,.12)",  bd: "rgba(255,93,143,.4)",   f: "R" },
  feature:   { a: "#a06bff", bg: "rgba(160,107,255,.14)", bd: "rgba(160,107,255,.4)",  f: "P" },
};

export default function SpookyEventCard({ kind, m, meta, onCardOpen }) {
  let scene, badge, title, chip, sceneHtml, onOpen;
  // Byline lines come from the REAL event data (not the design's placeholder names).
  const people = [];
  const add = (label, name) => { if (name) people.push([label, name]); };

  if (kind === "status") {
    const info = STATUS[norm(meta.status)] || STATUS.inprogress;
    scene = info.scene; badge = info.badge; title = info.title;
    chip = meta.status;
    sceneHtml = SPOOKY_SCENES[scene] || SPOOKY_SCENES.cauldron;
    onOpen = () => onCardOpen?.({ ...meta, at: m.createdAt });
    // Show assigned-to when there's an assignee, otherwise reported-by. (No "updated by".)
    if (meta.assignee) add("assigned to", meta.assignee);
    else add("reported by", meta.reporter);
  } else if (kind === "bug") {
    scene = "bug"; badge = "🐛 Bug logged"; title = "A bug crawls in…";
    chip = "Open"; sceneHtml = SPOOKY_BUG; onOpen = () => onCardOpen?.(meta);
    add("reported by", meta.by || meta.reporter);
  } else if (kind === "story") {
    // No bespoke story scene — borrow the feature scene's art, but say what it actually is.
    scene = "feature"; badge = "📝 Story created"; title = "A new tale is written";
    chip = "New"; sceneHtml = SPOOKY_FEATURE; onOpen = () => onCardOpen?.(meta);
    add("created by", meta.by || meta.reporter);
  } else {
    scene = "feature"; badge = "✨ Feature conjured"; title = "A new spell takes shape";
    chip = "New"; sceneHtml = SPOOKY_FEATURE; onOpen = () => onCardOpen?.(meta);
    add("requested by", meta.by || meta.reporter);
  }

  const st = SCENE[scene];
  const cls = scene; // scene == kind for bug/feature; a story borrows the feature scene's styling

  const fitRef = useRef(null);   // full-width wrapper (measured)
  const [onScreen, setOnScreen] = useState(true);
  const [zoom, setZoom] = useState(1);

  // Scale-to-fit via the CSS `zoom` property: the fixed 660px card is zoomed as ONE UNIT to fit
  // its container. Unlike transform:scale, `zoom` scales the LAYOUT box, so the card automatically
  // occupies the correct (scaled) space — no manual height measurement to get wrong. We only need
  // the container width.
  useLayoutEffect(() => {
    const wrap = fitRef.current;
    if (!wrap) return;
    const apply = () => { const w = wrap.clientWidth; if (w) setZoom(Math.min(1, w / 660)); };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, []);

  // Pause the (heavy) looping scene animations while scrolled out of view (off-screen CSS
  // animations otherwise keep ticking — the main cause of scroll lag on mobile).
  useEffect(() => {
    const el = fitRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => setOnScreen(e.isIntersecting), { rootMargin: "150px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={fitRef} className="spooky-fit">
      <div className={`spooky-card spooky-card--${cls}${onScreen ? "" : " spooky-card--paused"}`}
           style={{ "--ss-accent": st.a, "--ss-tintbg": st.bg, "--ss-tintbd": st.bd, zoom }}
           role="button" tabIndex={0} onClick={onOpen}
           onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}>
        <div className="spooky-card__inner">
          <div className="spooky-card__scene" aria-hidden dangerouslySetInnerHTML={{ __html: sceneHtml }} />
          <div className="spooky-card__scrim" aria-hidden />
          <div className="spooky-card__text">
            <div className="spooky-card__badgerow">
              <span className="spooky-card__badge">{badge}</span>
              {meta.id ? <span className="spooky-card__id">#{meta.id}</span> : null}
            </div>
            <h3 className={`spooky-card__title spooky-card__title--flick${st.f}`}>{title}</h3>
            {meta.title ? <div className="spooky-card__subject">{meta.title}</div> : null}
            {people.length > 0 && (
              <div className="spooky-card__bylines">
                {people.map(([label, name], i) => (
                  <div key={i} className="spooky-card__byline">{label} <b>{name}</b></div>
                ))}
              </div>
            )}
            <div className="spooky-card__foot"><span className="spooky-card__chip">{chip}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
